/**
 * Garage Configuration Management
 * Admin panel for managing dynamic configuration options
 */

import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Check, X, GripVertical, Download, Search, Info, ChevronDown } from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import ConfirmModal from '../modals/ConfirmModal';
import AlertModal from '../modals/AlertModal';
import { getAdminPersonaScopes, isDomainAdmin } from '../constants/roleAccess';
import { DISCOVER_FILTERS } from '../utils/discoverFilters';
import './GarageConfigManagement.css';
import PersonaWebhookSettings from './PersonaWebhookSettings';

const ALL_DISCOVER_FILTER_VALUES = DISCOVER_FILTERS.map((filter) => filter.value);

const MatrixCheckbox = ({ checked, indeterminate = false, ...props }) => (
  <input
    type="checkbox"
    checked={checked}
    ref={(element) => {
      if (element) element.indeterminate = Boolean(indeterminate);
    }}
    {...props}
  />
);

const CATEGORIES = [
  { value: 'persona_webhooks', group: 'notifications', label: 'Persona Webhooks',
    fullLabel: 'Notifications - Persona Webhooks', description: 'Manage RingCentral group destinations for request notifications.',
    usedIn: ['Request submissions', 'Request comments'] },
  // What users tell us about themselves
  {
    value: 'team',
    group: 'profile',
    label: 'Teams',
    fullLabel: 'User Profile - Teams',
    description: 'The teams a person can pick as their own (Sales, Customer Success, Product, …).',
    usedIn: ['Onboarding', 'Settings', 'Admin → Users'],
    note: 'Map each team to a persona to choose that user’s Discover filters. This personalizes filters only; Workday department remains the source of tool access.'
  },
  {
    value: 'user_profile_role',
    group: 'profile',
    label: 'Job Roles',
    fullLabel: 'User Profile - Job Roles',
    description: 'The job title a person can pick as their own (BDR, AE, SE, CSM, TAM, …).',
    usedIn: ['Onboarding', 'Settings', 'Admin → Users'],
    note: 'Each role needs a Category Type of Leadership or Operations, otherwise it will not appear in onboarding or Settings.'
  },

  // What people see on Discover
  {
    value: 'discover_filters',
    group: 'discover',
    label: 'Filter Visibility',
    fullLabel: 'Discover - Filter Visibility per Persona',
    description: 'Decide which Discover filters each persona is allowed to see.',
    usedIn: ['Discover (Simplified)', 'Discover (Advanced)'],
    note: 'This controls whether a filter is shown. The values inside each filter are maintained in the other sections.'
  },
  {
    value: 'journey',
    group: 'discover',
    label: 'Journey Stages',
    fullLabel: 'Discover - Journey Stages',
    description: 'Customer journey stages people filter tools by (Prospect, Qualify, Demo, …).',
    usedIn: ['Discover (Stage filter)', 'Admin → Tool form'],
    note: 'These are the stage values everyone sees on Discover. Sales Stages under Tool Catalog are a separate, sales-only tag on a tool.'
  },
  {
    value: 'capability',
    group: 'discover',
    label: 'Capabilities',
    fullLabel: 'Discover - Capabilities',
    description: 'What a tool can do (Research, Generate, Analyze, …).',
    usedIn: ['Discover (Capability filter)', 'Admin → Tool form']
  },

  // Metadata attached to each tool
  {
    value: 'tag',
    group: 'catalog',
    label: 'Tags',
    fullLabel: 'Tool Catalog - Tags',
    description: 'Free-form classification tags for tools.',
    usedIn: ['Discover (Tag filter)', 'Admin → Tool form', 'Tool detail']
  },
  {
    value: 'tool_target_role',
    group: 'catalog',
    label: 'Target Roles',
    fullLabel: 'Tool Catalog - Target Roles',
    description: 'The roles a tool is built for (Leadership, AE, SE, CSM, …).',
    usedIn: ['Discover (Role filter)', 'Admin → Tool form']
  },
  {
    value: 'integration',
    group: 'catalog',
    label: 'Integrations',
    fullLabel: 'Tool Catalog - Integrations',
    description: 'Third-party systems a tool connects to (SFDC, Gainsight, Gong, …).',
    usedIn: ['Admin → Tool form', 'Tool detail']
  },
  {
    value: 'environment',
    group: 'catalog',
    label: 'Environments',
    fullLabel: 'Tool Catalog - Environments',
    description: 'Where a tool is deployed (PRD, DEV, TEST, STAGING).',
    usedIn: ['Admin → Tool form', 'Tool detail']
  },
  {
    value: 'model',
    group: 'catalog',
    label: 'AI Models',
    fullLabel: 'Tool Catalog - AI Models',
    description: 'The models a tool runs on (GPT-4, Claude, Gemini, …).',
    usedIn: ['Admin → Tool form', 'Tool detail']
  },
  {
    value: 'sales_stage',
    group: 'catalog',
    label: 'Sales Stages',
    fullLabel: 'Tool Catalog - Sales Stages',
    description: 'Sales pipeline stage a tool supports (qualify, demo, proposal, onboard, expand, retain).',
    usedIn: ['Admin → Tool form', 'Sales views'],
    note: 'Sales-only tagging. For the stage filter everyone sees on Discover, use Journey Stages instead.'
  },

  // Personas and the Workday mapping behind them
  {
    value: 'persona',
    group: 'mapping',
    label: 'Personas',
    fullLabel: 'Organization Mapping - Personas',
    description: 'The personas the whole app is scoped by (Sales, Customer Success, Product, …).',
    usedIn: ['Discover visibility', 'Admin → Users', 'Admin → Tool form'],
    note: 'A persona is also what a Persona Admin is scoped to. Renaming or deactivating one affects access, so change these carefully.'
  },
  {
    value: 'department',
    group: 'mapping',
    label: 'Departments',
    fullLabel: 'Organization Mapping - Departments',
    description: 'Workday business units, each mapped to a parent persona.',
    usedIn: ['Workday sync', 'Discover visibility'],
    note: 'A user\'s Workday department decides their persona, which decides the Discover filters they see. Every department needs a parent persona.'
  },
];

const CONFIG_GROUPS = [
  { id: 'notifications', label: 'Notifications', description: 'Group notification destinations.' },
  {
    id: 'profile',
    label: 'User Profile & Onboarding',
    description: 'What people pick about themselves.'
  },
  {
    id: 'discover',
    label: 'Discover Experience',
    description: 'Which filters people see and what those filters offer.'
  },
  {
    id: 'catalog',
    label: 'Tool Catalog',
    description: 'Metadata attached to each tool.'
  },
  {
    id: 'mapping',
    label: 'Organization Mapping — Advanced',
    description: 'Personas and Workday departments. Affects access.'
  },
];

const CONFIG_NAV_COLLAPSE_KEY = 'garage-config-nav-collapsed';

const loadCollapsedNavGroups = (selectedGroupId) => {
  try {
    const raw = localStorage.getItem(CONFIG_NAV_COLLAPSE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return new Set(Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : []);
    }
  } catch {
    // Fall through to the default: only the selected group starts open.
  }
  return new Set(CONFIG_GROUPS.map((group) => group.id).filter((id) => id !== selectedGroupId));
};

const GarageConfigManagement = ({
  onConfigChange,
  userRole,
  accessPersona = null,
  accessPersonas = [],
}) => {
  const domainAdmin = isDomainAdmin(userRole);
  const adminPersonaScopes = getAdminPersonaScopes(
    userRole,
    accessPersonas,
    accessPersona
  );
  const normalizedAdminPersonaScopes = adminPersonaScopes.map(
    (persona) => persona.toLowerCase()
  );
  const isSalesAdmin = domainAdmin
    && normalizedAdminPersonaScopes.includes('sales');
  const [selectedCategory, setSelectedCategory] = useState(
    () => (isSalesAdmin ? 'sales_stage' : domainAdmin ? 'persona' : 'user_profile_role')
  );
  const [categorySearch, setCategorySearch] = useState('');
  const [collapsedNavGroups, setCollapsedNavGroups] = useState(() => {
    const initialCategory = domainAdmin ? 'discover_filters' : 'user_profile_role';
    const selectedGroupId = CATEGORIES.find((category) => category.value === initialCategory)?.group;
    return loadCollapsedNavGroups(selectedGroupId);
  });
  const [options, setOptions] = useState([]);
  const [personaParents, setPersonaParents] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [savingPersonaIds, setSavingPersonaIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [formData, setFormData] = useState({
    value: '',
    label: '',
    description: '',
    displayOrder: 0,
    isActive: true,
    parentId: null,
    categoryType: '',
    discoverFilters: []
  });

  useEffect(() => {
    if (domainAdmin) {
      setSelectedCategory('discover_filters');
    }
  }, [domainAdmin]);

  // Modal states
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    variant: 'default'
  });

  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info'
  });

  // Workday business unit import (departments only)
  const [showImport, setShowImport] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [importParentId, setImportParentId] = useState('');

  useEffect(() => {
    localStorage.setItem(CONFIG_NAV_COLLAPSE_KEY, JSON.stringify([...collapsedNavGroups]));
  }, [collapsedNavGroups]);

  const toggleNavGroup = (groupId) => {
    setCollapsedNavGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const selectCategory = (value) => {
    setSelectedCategory(value);
    const groupId = CATEGORIES.find((category) => category.value === value)?.group;
    if (!groupId) return;
    setCollapsedNavGroups((prev) => {
      if (!prev.has(groupId)) return prev;
      const next = new Set(prev);
      next.delete(groupId);
      return next;
    });
  };

  // Load options when category changes
  useEffect(() => {
    loadOptions();
  }, [selectedCategory]);

  const loadOptions = async () => {
    if (selectedCategory === 'persona_webhooks') { setOptions([]); setLoading(false); return; }
    setLoading(true);
    try {
      // The Discover filter matrix is persona-backed, so it reuses the persona options.
      const sourceCategory = selectedCategory === 'discover_filters' ? 'persona' : selectedCategory;
      const data = await garageAPI.getConfigOptions(sourceCategory, true); // Include inactive
      setOptions(data);
      if (selectedCategory === 'department' || selectedCategory === 'team') {
        const personas = await garageAPI.getConfigOptions('persona', true);
        setPersonaParents(personas || []);
      }
      if (selectedCategory === 'discover_filters') {
        const departments = await garageAPI.getConfigOptions('department', true);
        setDepartmentOptions(departments || []);
      }
    } catch (error) {
      console.error('Failed to load config options:', error);
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to load options. Please try again.',
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const departmentsForPersona = (personaId) =>
    departmentOptions.filter((department) => String(department.parentId) === String(personaId));

  const selectedFiltersFor = (persona) =>
    Array.isArray(persona?.discoverFilters) ? persona.discoverFilters : [];

  const sameDiscoverFilters = (left, right) => {
    if (left.length !== right.length) return false;
    const selected = new Set(left);
    return right.every((value) => selected.has(value));
  };

  const persistDiscoverFilters = async (updates) => {
    if (!canManageCategory('persona') || updates.length === 0) return;

    const previousById = Object.fromEntries(
      updates.map(({ persona }) => [persona.id, selectedFiltersFor(persona)])
    );
    const nextById = Object.fromEntries(
      updates.map(({ persona, nextFilters }) => [persona.id, nextFilters])
    );

    setOptions((prev) =>
      prev.map((option) =>
        nextById[option.id] ? { ...option, discoverFilters: nextById[option.id] } : option
      )
    );
    setSavingPersonaIds(updates.map(({ persona }) => persona.id));
    try {
      await Promise.all(
        updates.map(({ persona, nextFilters }) =>
          garageAPI.updateConfigOption(persona.id, { discoverFilters: nextFilters })
        )
      );
      if (onConfigChange) onConfigChange();
    } catch (error) {
      console.error('Failed to update Discover filters:', error);
      setOptions((prev) =>
        prev.map((option) =>
          previousById[option.id]
            ? { ...option, discoverFilters: previousById[option.id] }
            : option
        )
      );
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message:
          'Failed to update Discover filters: ' +
          (error.response?.data?.detail || error.message),
        variant: 'error'
      });
    } finally {
      setSavingPersonaIds([]);
    }
  };

  const handleToggleDiscoverFilter = (persona, facet) => {
    const current = selectedFiltersFor(persona);
    const nextFilters = current.includes(facet)
      ? current.filter((value) => value !== facet)
      : [...current, facet];
    persistDiscoverFilters([{ persona, nextFilters }]);
  };

  const handleTogglePersonaRow = (persona) => {
    const current = selectedFiltersFor(persona);
    const allSelected = ALL_DISCOVER_FILTER_VALUES.every((facet) => current.includes(facet));
    persistDiscoverFilters([{
      persona,
      nextFilters: allSelected ? [] : [...ALL_DISCOVER_FILTER_VALUES]
    }]);
  };

  const handleToggleFilterColumn = (facet) => {
    const allSelected = visibleOptions.every((persona) =>
      selectedFiltersFor(persona).includes(facet)
    );
    const updates = visibleOptions
      .map((persona) => {
        const current = selectedFiltersFor(persona);
        const nextFilters = allSelected
          ? current.filter((value) => value !== facet)
          : current.includes(facet)
            ? current
            : [...current, facet];
        return { persona, nextFilters };
      })
      .filter(({ persona, nextFilters }) =>
        !sameDiscoverFilters(selectedFiltersFor(persona), nextFilters)
      );
    persistDiscoverFilters(updates);
  };

  const handleToggleAllFilters = () => {
    const allSelected = visibleOptions.every((persona) =>
      ALL_DISCOVER_FILTER_VALUES.every((facet) => selectedFiltersFor(persona).includes(facet))
    );
    persistDiscoverFilters(
      visibleOptions
        .map((persona) => ({
          persona,
          nextFilters: allSelected ? [] : [...ALL_DISCOVER_FILTER_VALUES]
        }))
        .filter(({ persona, nextFilters }) =>
          !sameDiscoverFilters(selectedFiltersFor(persona), nextFilters)
        )
    );
  };

  const personaLabelForParent = (parentId) => {
    if (!parentId) return null;
    const match = personaParents.find((p) => p.id === parentId);
    return match ? match.label : null;
  };

  const openImport = async () => {
    setShowImport(true);
    setImportLoading(true);
    setImportParentId('');
    try {
      const units = await garageAPI.getWorkdayBusinessUnits();
      setBusinessUnits(units || []);
      setSelectedUnits((units || []).filter((u) => !u.exists).map((u) => u.businessUnit));
    } catch (error) {
      console.error('Failed to load Workday business units:', error);
      setShowImport(false);
      setAlertModal({
        isOpen: true,
        title: 'Workday unavailable',
        message:
          error.response?.data?.detail ||
          'Could not read business units from Workday. Please try again.',
        variant: 'error'
      });
    } finally {
      setImportLoading(false);
    }
  };

  const toggleUnit = (businessUnit) => {
    setSelectedUnits((prev) =>
      prev.includes(businessUnit)
        ? prev.filter((u) => u !== businessUnit)
        : [...prev, businessUnit]
    );
  };

  const handleImport = async () => {
    if (selectedUnits.length === 0) return;
    setImporting(true);
    try {
      const result = await garageAPI.importDepartmentsFromWorkday({
        businessUnits: selectedUnits,
        parentId: importParentId || null,
      });
      setShowImport(false);
      loadOptions();
      if (onConfigChange) onConfigChange();
      setAlertModal({
        isOpen: true,
        title: 'Import complete',
        message: `Created ${result.created.length} department(s)${
          result.skipped.length ? `, skipped ${result.skipped.length} already present` : ''
        }.`,
        variant: 'success'
      });
    } catch (error) {
      console.error('Failed to import departments:', error);
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: error.response?.data?.detail || 'Failed to import departments.',
        variant: 'error'
      });
    } finally {
      setImporting(false);
    }
  };

  const canManageCategory = (category) =>
    !domainAdmin ||
    (isSalesAdmin && category === 'sales_stage') ||
    (selectedCategory === 'discover_filters' && category === 'persona');
  const canAddInCategory = canManageCategory(selectedCategory);
  const canEditOption = (option) => canManageCategory(option.category);
  const canDeleteOption = (option) => canManageCategory(option.category);

  const handleAdd = () => {
    if (!canAddInCategory) return;
    setEditingOption(null);
    setFormData({
      value: '',
      label: '',
      description: '',
      displayOrder: options.length,
      isActive: true,
      parentId: null,
      categoryType: '',
      discoverFilters: []
    });
    setShowForm(true);
  };

  const handleEdit = (option) => {
    if (!canEditOption(option)) return;
    setEditingOption(option);
    setFormData({
      value: option.value,
      label: option.label,
      description: option.description || '',
      displayOrder: option.displayOrder,
      isActive: option.isActive,
      parentId: option.parentId || null,
      categoryType: option.categoryType || '',
      discoverFilters: Array.isArray(option.discoverFilters) ? option.discoverFilters : []
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.value || !formData.label) {
        setAlertModal({
          isOpen: true,
          title: 'Validation Error',
          message: 'Value and Label are required',
          variant: 'error'
        });
        return;
      }

      if (
        (selectedCategory === 'department' || selectedCategory === 'team') &&
        !formData.parentId
      ) {
        setAlertModal({
          isOpen: true,
          title: 'Validation Error',
          message: `Parent persona is required for ${
            selectedCategory === 'department' ? 'departments' : 'teams'
          }`,
          variant: 'error'
        });
        return;
      }

      const payload = {
        category: selectedCategory,
        value: formData.value,
        label: formData.label,
        description: formData.description || null,
        displayOrder: formData.displayOrder,
        isActive: formData.isActive,
        parentId: formData.parentId || null,
        categoryType: formData.categoryType || null,
        discoverFilters: selectedCategory === 'persona' ? formData.discoverFilters : []
      };

      if (editingOption) {
        await garageAPI.updateConfigOption(editingOption.id, payload);
      } else {
        await garageAPI.createConfigOption(payload);
      }

      setShowForm(false);
      loadOptions();
      // Notify parent component to refresh its dynamic options
      if (onConfigChange) {
        onConfigChange();
      }
    } catch (error) {
      console.error('Failed to save option:', error);
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to save option: ' + (error.response?.data?.detail || error.message),
        variant: 'error'
      });
    }
  };

  const handleDelete = async (option) => {
    if (!canDeleteOption(option)) return;
    setConfirmModal({
      isOpen: true,
      title: 'Delete Option',
      message: `Delete "${option.label}"? This cannot be undone.`,
      variant: 'danger',
      onConfirm: async () => {
        try {
          await garageAPI.deleteConfigOption(option.id);
          loadOptions();
          // Notify parent component to refresh its dynamic options
          if (onConfigChange) {
            onConfigChange();
          }
          setConfirmModal({ ...confirmModal, isOpen: false });
          setAlertModal({
            isOpen: true,
            title: 'Success',
            message: 'Option deleted successfully!',
            variant: 'success'
          });
        } catch (error) {
          console.error('Failed to delete option:', error);
          setConfirmModal({ ...confirmModal, isOpen: false });
          setAlertModal({
            isOpen: true,
            title: 'Error',
            message: 'Failed to delete option. Please try again.',
            variant: 'error'
          });
        }
      }
    });
  };

  const handleToggleActive = async (option) => {
    if (!canEditOption(option)) return;
    try {
      await garageAPI.updateConfigOption(option.id, {
        isActive: !option.isActive
      });
      loadOptions();
      // Notify parent component to refresh its dynamic options
      if (onConfigChange) {
        onConfigChange();
      }
    } catch (error) {
      console.error('Failed to toggle active status:', error);
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to update option. Please try again.',
        variant: 'error'
      });
    }
  };

  const visibleCategories = domainAdmin
    ? CATEGORIES.filter(
      c => c.value === 'persona_webhooks' || c.value === 'discover_filters' || (isSalesAdmin && c.value === 'sales_stage')
    )
    : CATEGORIES;
  const selectedCategoryInfo = visibleCategories.find(c => c.value === selectedCategory);
  const normalizedSearch = categorySearch.trim().toLowerCase();
  const matchesSearch = (category) => {
    if (!normalizedSearch) return true;
    return [category.label, category.fullLabel, category.description, ...(category.usedIn || [])]
      .join(' ')
      .toLowerCase()
      .includes(normalizedSearch);
  };
  const navGroups = CONFIG_GROUPS
    .map((group) => ({
      ...group,
      categories: visibleCategories.filter(
        (category) => category.group === group.id && matchesSearch(category)
      )
    }))
    .filter((group) => group.categories.length > 0);
  const selectedGroupInfo = CONFIG_GROUPS.find((group) => group.id === selectedCategoryInfo?.group);
  const isDiscoverFilterView = selectedCategory === 'discover_filters';
  const visibleOptions = (domainAdmin &&
    (selectedCategory === 'persona' || selectedCategory === 'discover_filters'))
    ? options.filter(
      (option) =>
        normalizedAdminPersonaScopes.includes(
          (option.value || '').trim().toLowerCase()
        ) ||
        normalizedAdminPersonaScopes.includes(
          (option.label || '').trim().toLowerCase()
        )
    )
    : options;
  const matrixBusy = savingPersonaIds.length > 0;
  const matrixCanEdit = canManageCategory('persona') && !matrixBusy;
  const matrixSelectedCount = visibleOptions.reduce(
    (count, persona) => count + selectedFiltersFor(persona).length,
    0
  );
  const matrixTotalCount = visibleOptions.length * ALL_DISCOVER_FILTER_VALUES.length;
  const allFiltersChecked = matrixTotalCount > 0 && matrixSelectedCount === matrixTotalCount;
  const allFiltersIndeterminate = matrixSelectedCount > 0 && !allFiltersChecked;

  return (
    <div className="garage-config-management">
      <div className="config-header">
        <div>
          <h2>Configuration Management</h2>
          <p className="config-subtitle">
            Every dropdown and filter in the AI Garage, grouped by where it shows up
          </p>
        </div>
      </div>

      {domainAdmin && (
        <div className="config-scope-banner">
          <strong>{adminPersonaScopes.join(', ')} Admin View:</strong>{' '}
          {isSalesAdmin
            ? 'You can manage persona webhooks, Discover filter visibility for your assigned personas, and Sales Stages.'
            : `You can manage persona webhooks and Discover filter visibility for ${adminPersonaScopes.join(', ')}. Other personas are hidden.`}
        </div>
      )}

      <div className="config-layout">
        <nav className="config-nav" aria-label="Configuration sections">
          <div className="config-nav-search">
            <Search size={14} />
            <input
              type="search"
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              placeholder="Search settings"
              aria-label="Search configuration settings"
            />
          </div>
          {navGroups.length === 0 ? (
            <p className="config-nav-empty">No settings match “{categorySearch}”.</p>
          ) : (
            navGroups.map((group) => {
              const isSearching = Boolean(normalizedSearch);
              const isOpen = isSearching || !collapsedNavGroups.has(group.id);
              const selectedInGroup = group.categories.find(
                (category) => category.value === selectedCategory
              );
              return (
              <div key={group.id} className={`config-nav-group ${isOpen ? 'is-open' : 'is-collapsed'}`}>
                <button
                  type="button"
                  className="config-nav-group-toggle"
                  aria-expanded={isOpen}
                  disabled={isSearching}
                  onClick={() => toggleNavGroup(group.id)}
                >
                  <ChevronDown size={16} className="config-nav-group-chevron" />
                  <span className="config-nav-group-copy">
                    <strong>{group.label}</strong>
                    <small>
                      {isOpen
                        ? group.description
                        : `${group.categories.length} setting${group.categories.length === 1 ? '' : 's'}${
                          selectedInGroup ? ` · ${selectedInGroup.label}` : ''
                        }`}
                    </small>
                  </span>
                </button>
                {isOpen && (
                <ul>
                  {group.categories.map((category) => (
                    <li key={category.value}>
                      <button
                        type="button"
                        className={`config-nav-item ${selectedCategory === category.value ? 'active' : ''}`}
                        onClick={() => selectCategory(category.value)}
                        aria-current={selectedCategory === category.value}
                      >
                        <span className="config-nav-item-label">{category.label}</span>
                        <span className="config-nav-item-used">
                          {(category.usedIn || []).join(' · ')}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                )}
              </div>
              );
            })
          )}
        </nav>

        <div className="config-content">
        {selectedCategory === 'persona_webhooks' ? (
          <PersonaWebhookSettings key={`${userRole}:${adminPersonaScopes.join('|')}`} />
        ) : <>
        <div className="config-toolbar">
          <div className="config-toolbar-info">
            {selectedGroupInfo && (
              <span className="config-toolbar-group">{selectedGroupInfo.label}</span>
            )}
            <h3>{selectedCategoryInfo?.label}</h3>
            {selectedCategoryInfo?.description && (
              <p className="config-toolbar-description">{selectedCategoryInfo.description}</p>
            )}
            {(selectedCategoryInfo?.usedIn || []).length > 0 && (
              <div className="config-used-in">
                <span className="config-used-in-label">Used in</span>
                {selectedCategoryInfo.usedIn.map((place) => (
                  <span key={place} className="config-used-in-chip">{place}</span>
                ))}
              </div>
            )}
            <p className="config-count">
              {isDiscoverFilterView
                ? `${visibleOptions.length} personas`
                : `${visibleOptions.length} options`}
            </p>
          </div>
          <div className="config-toolbar-actions">
            {selectedCategory === 'department' && !domainAdmin && (
              <button className="garage-btn garage-btn-secondary" onClick={openImport}>
                <Download size={16} />
                Import from Workday
              </button>
            )}
            {canAddInCategory && !isDiscoverFilterView && (
              <button className="garage-btn garage-btn-primary" onClick={handleAdd}>
                <Plus size={16} />
                Add Option
              </button>
            )}
          </div>
        </div>

        {selectedCategoryInfo?.note && (
          <p className="config-note">
            <Info size={14} />
            <span>{selectedCategoryInfo.note}</span>
          </p>
        )}

        {loading ? (
          <div className="config-loading">Loading...</div>
        ) : isDiscoverFilterView ? (
          <div className="config-discover-matrix">
            <p className="config-discover-matrix-help">
              A user sees a filter only when their Workday department maps to a persona that has
              the filter ticked here. Stage (Journey) controls the filter bubbles in Simplified
              view, and its stage values live under{' '}
              <strong>Discover Experience → Journey Stages</strong>. Departments are mapped under{' '}
              <strong>Organization Mapping → Departments</strong>. Use the checkboxes in the
              header to select a whole column, or the one next to a persona for a whole row.
              Changes save immediately.
            </p>
            {visibleOptions.length === 0 ? (
              <div className="config-empty">
                <p>No personas configured yet. Add one under Organization Mapping → Personas.</p>
              </div>
            ) : (
              <table className="config-discover-table">
                <thead>
                  <tr>
                    <th scope="col" className="config-discover-persona-col">
                      <label className="config-discover-select-all">
                        <MatrixCheckbox
                          aria-label="Select all filters for all personas"
                          checked={allFiltersChecked}
                          indeterminate={allFiltersIndeterminate}
                          disabled={!matrixCanEdit}
                          onChange={handleToggleAllFilters}
                        />
                        <span>Persona</span>
                      </label>
                    </th>
                    {DISCOVER_FILTERS.map((filter) => {
                      const selectedCount = visibleOptions.filter((persona) =>
                        selectedFiltersFor(persona).includes(filter.value)
                      ).length;
                      const checked =
                        visibleOptions.length > 0 && selectedCount === visibleOptions.length;
                      const indeterminate =
                        selectedCount > 0 && selectedCount < visibleOptions.length;
                      return (
                        <th scope="col" key={filter.value}>
                          <label className="config-discover-col-header">
                            <MatrixCheckbox
                              aria-label={`Select ${filter.label} for all personas`}
                              checked={checked}
                              indeterminate={indeterminate}
                              disabled={!matrixCanEdit}
                              onChange={() => handleToggleFilterColumn(filter.value)}
                            />
                            <span>{filter.label}</span>
                          </label>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {visibleOptions.map((persona) => {
                    const selected = selectedFiltersFor(persona);
                    const mapped = departmentsForPersona(persona.id);
                    const rowChecked = ALL_DISCOVER_FILTER_VALUES.every((facet) =>
                      selected.includes(facet)
                    );
                    const rowIndeterminate = selected.length > 0 && !rowChecked;
                    return (
                      <tr
                        key={persona.id}
                        className={savingPersonaIds.includes(persona.id) ? 'saving' : ''}
                      >
                        <th scope="row">
                          <label className="config-discover-row-header">
                            <MatrixCheckbox
                              aria-label={`Select all filters for ${persona.label}`}
                              checked={rowChecked}
                              indeterminate={rowIndeterminate}
                              disabled={!matrixCanEdit}
                              onChange={() => handleTogglePersonaRow(persona)}
                            />
                            <span>
                              <strong>{persona.label}</strong>
                              {!persona.isActive && (
                                <span className="config-option-inactive-badge">Inactive</span>
                              )}
                              <small>
                                {mapped.length === 0
                                  ? 'No departments mapped'
                                  : `${mapped.length} department${mapped.length === 1 ? '' : 's'}: ${mapped
                                    .map((department) => department.label)
                                    .join(', ')}`}
                              </small>
                            </span>
                          </label>
                        </th>
                        {DISCOVER_FILTERS.map((filter) => (
                          <td key={filter.value}>
                            <MatrixCheckbox
                              aria-label={`${filter.label} filter for ${persona.label}`}
                              checked={selected.includes(filter.value)}
                              disabled={!matrixCanEdit}
                              onChange={() => handleToggleDiscoverFilter(persona, filter.value)}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <div className="config-options-list">
            {visibleOptions.length === 0 ? (
              <div className="config-empty">
                <p>No options found for this category.</p>
                {canAddInCategory && (
                  <button className="garage-btn garage-btn-secondary" onClick={handleAdd}>
                    <Plus size={16} />
                    Add First Option
                  </button>
                )}
              </div>
            ) : (
              visibleOptions.map((option) => {
                const editAllowed = canEditOption(option);
                const deleteAllowed = canDeleteOption(option);
                return (
                  <div key={option.id} className={`config-option-card ${!option.isActive ? 'inactive' : ''}`}>
                    <div className="config-option-drag">
                      <GripVertical size={16} />
                    </div>
                    <div className="config-option-info">
                      <div className="config-option-header">
                        <strong>{option.label}</strong>
                        <code className="config-option-value">{option.value}</code>
                        {!option.isActive && <span className="config-option-inactive-badge">Inactive</span>}
                      </div>
                      {option.description && (
                        <p className="config-option-description">{option.description}</p>
                      )}
                      <div className="config-option-meta">
                        <span>Order: {option.displayOrder}</span>
                        {(selectedCategory === 'department' ||
                          selectedCategory === 'team') && (
                          <span>
                            Persona: {personaLabelForParent(option.parentId) || 'Unassigned'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="config-option-actions">
                      <button
                        className={`config-option-toggle ${option.isActive ? 'active' : 'inactive'}`}
                        onClick={() => handleToggleActive(option)}
                        disabled={!editAllowed}
                        title={editAllowed ? (option.isActive ? 'Deactivate' : 'Activate') : 'Persona Admin cannot manage this option'}
                        style={!editAllowed ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                      >
                        {option.isActive ? 'Active' : 'Inactive'}
                      </button>
                      <button
                        className="config-option-edit"
                        onClick={() => handleEdit(option)}
                        disabled={!editAllowed}
                        title={editAllowed ? 'Edit' : 'Persona Admin cannot edit this option'}
                        style={!editAllowed ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        className="config-option-delete"
                        onClick={() => handleDelete(option)}
                        disabled={!deleteAllowed}
                        title={deleteAllowed ? 'Delete' : 'Persona Admin cannot delete this option'}
                        style={!deleteAllowed ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
        </>}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="config-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="config-modal" onClick={(e) => e.stopPropagation()}>
            <div className="config-modal-header">
              <h3>
                {editingOption ? 'Edit' : 'Add'}{' '}
                {selectedCategoryInfo?.fullLabel || 'Option'}
              </h3>
              <button className="config-modal-close" onClick={() => setShowForm(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="config-modal-body">
              <div className="config-form-group">
                <label>Value (Internal) *</label>
                <input
                  type="text"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  placeholder="e.g., 'AE' or 'Account Executive'"
                  disabled={!!editingOption}
                />
                <small>Used in code and database. Cannot be changed after creation.</small>
              </div>
              <div className="config-form-group">
                <label>Label (Display) *</label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  placeholder="e.g., 'Account Executive'"
                />
                <small>Shown to users in dropdowns and forms.</small>
              </div>
              <div className="config-form-group">
                <label>Description (Optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description or help text"
                  rows="2"
                />
              </div>

              {/* Departments and profile teams map to a persona for Discover filter visibility. */}
              {(selectedCategory === 'department' ||
                selectedCategory === 'team') && (
                <div className="config-form-group">
                  <label>Parent Persona *</label>
                  <select
                    value={formData.parentId || ''}
                    onChange={(e) => setFormData({ ...formData, parentId: e.target.value || null })}
                    required
                  >
                    <option value="">Select persona...</option>
                    {personaParents.map((opt) => (
                      <option
                        key={opt.id}
                        value={opt.id}
                        disabled={selectedCategory === 'team' && !opt.isActive}
                      >
                        {opt.label}{!opt.isActive ? ' (Inactive)' : ''}
                      </option>
                    ))}
                  </select>
                  <small>
                    {selectedCategory === 'department'
                      ? 'Department belongs under this persona (Workday-mapped access).'
                      : 'Picking this team uses the persona’s filter visibility. It does not grant tool or admin access.'}
                  </small>
                </div>
              )}

              {/* Hierarchical Fields - Show for roles and teams */}
              {(selectedCategory === 'role' || selectedCategory === 'team' || selectedCategory === 'user_profile_role') && (
                <>
                  {selectedCategory !== 'team' && (
                    <div className="config-form-group">
                      <label>Parent Category (Optional)</label>
                      <select
                        value={formData.parentId || ''}
                        onChange={(e) => setFormData({ ...formData, parentId: e.target.value || null })}
                      >
                        <option value="">None (Top Level)</option>
                        {options
                          .filter(opt => opt.category === selectedCategory + '_category')
                          .map(opt => (
                            <option key={opt.id} value={opt.id}>{opt.label}</option>
                          ))}
                      </select>
                      <small>Group this {selectedCategory} under a parent category (e.g., Leadership, Operations)</small>
                    </div>
                  )}
                  <div className="config-form-group">
                    <label>Category Type {selectedCategory === 'user_profile_role' ? '*' : '(Optional)'}</label>
                    <select
                      value={formData.categoryType}
                      onChange={(e) => setFormData({ ...formData, categoryType: e.target.value })}
                      required={selectedCategory === 'user_profile_role'}
                    >
                      <option value="">-- Select Category Type --</option>
                      <option value="leadership">Leadership</option>
                      <option value="operations">Operations</option>
                    </select>
                    <small>
                      {selectedCategory === 'user_profile_role'
                        ? 'Required: Choose "Leadership" or "Operations" to display this role in onboarding and settings'
                        : 'Sub-category identifier for filtering and grouping'}
                    </small>
                  </div>
                </>
              )}

              <div className="config-form-row">
                <div className="config-form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={formData.displayOrder}
                    onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                  />
                  <small>Lower numbers appear first.</small>
                </div>
                <div className="config-form-group">
                  <label>Status</label>
                  <label className="config-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="config-modal-footer">
              <button className="garage-btn garage-btn-secondary" onClick={() => setShowForm(false)}>
                Cancel
              </button>
              <button className="garage-btn garage-btn-primary" onClick={handleSave}>
                <Check size={16} />
                {editingOption ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Workday Business Unit Import */}
      {showImport && (
        <div className="config-modal-overlay" onClick={() => !importing && setShowImport(false)}>
          <div className="config-modal config-modal-import" onClick={(e) => e.stopPropagation()}>
            <div className="config-modal-header">
              <h3>Import departments from Workday</h3>
              <button className="config-modal-close" onClick={() => setShowImport(false)} disabled={importing}>
                <X size={20} />
              </button>
            </div>
            <div className="config-modal-body">
              {importLoading ? (
                <div className="config-loading">Loading business units...</div>
              ) : (
                <>
                  <div className="config-form-group">
                    <label>Parent Persona (Optional)</label>
                    <select
                      value={importParentId}
                      onChange={(e) => setImportParentId(e.target.value)}
                    >
                      <option value="">None — assign later</option>
                      {personaParents.map((opt) => (
                        <option key={opt.id} value={opt.id}>{opt.label}</option>
                      ))}
                    </select>
                    <small>Applied to every department in this import. You can change each one afterwards.</small>
                  </div>

                  <div className="config-import-section">
                    <div className="config-import-heading">
                      <span>Business units</span>
                      <span className="config-import-count">
                        {selectedUnits.length} of {businessUnits.length} selected
                      </span>
                    </div>
                    <div className="config-import-list">
                      {businessUnits.map((unit) => (
                        <label
                          key={unit.businessUnit}
                          className={`config-import-row ${unit.exists ? 'is-existing' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={unit.exists || selectedUnits.includes(unit.businessUnit)}
                            disabled={unit.exists}
                            onChange={() => toggleUnit(unit.businessUnit)}
                          />
                          <span className="config-import-name">{unit.businessUnit}</span>
                          <span className="config-import-meta">
                            {unit.exists ? 'Already added' : `${unit.employeeCount} employees`}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="config-modal-footer">
              <button
                className="garage-btn garage-btn-secondary"
                onClick={() => setShowImport(false)}
                disabled={importing}
              >
                Cancel
              </button>
              <button
                className="garage-btn garage-btn-primary"
                onClick={handleImport}
                disabled={importing || importLoading || selectedUnits.length === 0}
              >
                <Check size={16} />
                {importing ? 'Importing...' : `Import ${selectedUnits.length}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />
    </div>
  );
};

export default GarageConfigManagement;
